/** O recibo POR LINHA da importação da configuração da empresa.
 *
 *  O recibo de canais/veículos e o de publicadores só mostram contadores
 *  (o laço é "baixar as recusadas"). A planilha de configuração tem dezessete
 *  abas num arquivo só, então o contador não diz ONDE: cada linha aparece
 *  aqui com a ABA (o nome que a pessoa vê na planilha, traduzido da chave
 *  `catalog_kind`), a linha dela no arquivo, o nome do item, o desfecho e o
 *  motivo. Carrega sob demanda (a lista de execuções não pede as linhas), e
 *  filtra por desfecho no servidor. */

import { useCallback, useEffect, useState } from "react";
import {
  apiClient,
  type CatalogImportRow
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { companySheetName } from "@/shared/vocabulary/company-config-sheets";
import { Select } from "@/shared/components/Select";

const PAGE_SIZE = 50;

const OUTCOME_LABELS: Record<string, string> = {
  created: "criado",
  updated: "atualizado",
  alias_created: "apelido",
  rejected: "recusado",
  skipped: "pulado"
};

/** O que identifica a linha pra quem a lê: o concorrente (aba de
 *  concorrentes) ou o nome do item. Cai na primeira célula preenchida. */
export function rowTitle(raw: Record<string, unknown>): string {
  for (const column of ["Nome", "Concorrente", "Marca", "Categoria", "Tarefa"]) {
    const value = raw[column];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "—";
}

type Props = { token: string; importId: number };

export function CompanyImportReceipt({ token, importId }: Props) {
  const [outcome, setOutcome] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<CatalogImportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const result = await apiClient.catalogImportRows(token, importId, {
        outcome: outcome || undefined,
        page,
        size: PAGE_SIZE
      });
      setRows(result.items);
      setTotal(result.total);
    } catch (error) {
      setFailure(errorMessage(error, "Não foi possível carregar o recibo."));
    } finally {
      setLoading(false);
    }
  }, [token, importId, outcome, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="import-receipt">
      <label className="form-field import-receipt-filter">
        <span>Desfecho</span>
        <Select
          value={outcome}
          onChange={event => {
            setOutcome(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos</option>
          {Object.entries(OUTCOME_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </label>

      {failure ? (
        <ApiState kind="error" onRetry={() => void load()}>
          {failure}
        </ApiState>
      ) : loading && rows.length === 0 ? (
        <ApiState kind="loading">Carregando o recibo…</ApiState>
      ) : rows.length === 0 ? (
        <p className="section-hint">Nenhuma linha com este desfecho.</p>
      ) : (
        <table className="ai-admin-table">
          <thead>
            <tr>
              <th>Aba</th>
              <th>Item</th>
              <th>Desfecho</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.row_index}>
                <td>{companySheetName(row.catalog_kind)}</td>
                <td>{rowTitle(row.raw)}</td>
                <td>
                  {row.outcome === null
                    ? "pendente"
                    : (OUTCOME_LABELS[row.outcome] ?? row.outcome)}
                </td>
                <td>{row.message ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Pager
        page={page}
        size={PAGE_SIZE}
        onPage={setPage}
        count={{ total }}
        busy={loading}
      />
    </div>
  );
}
